import * as React from 'react';
import { Box, Grid, Paper, Skeleton, Tooltip, Typography, Divider, LinearProgress } from '@mui/material';
import { Refresh, Description, MonetizationOn, TrendingUp, Verified, SwapHoriz, Warning, Assignment } from '@mui/icons-material';
import { useAuthUser, useAuthHeader } from '../Services/Auth/sessionAuth';
import Utilisateur from '../Models/Utilisateur';
import { Table as AntTable, Tabs, Tag } from 'antd';
import {
  AreaChart as ReAreaChart, Area, BarChart as ReBarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
  RadarChart as ReRadarChart, Radar, PolarGrid, PolarRadiusAxis,
  ComposedChart as ReComposedChart, Line, RadialBarChart as ReRadialBarChart, RadialBar,
  PolarAngleAxis as _PolarAngleAxis,
} from 'recharts';
import axios from 'axios';
import datas from '../Data/Data.json';
import { Crypter } from '../Functions/Security/Crypter';
import IntervaleDate from '../Models/IntervaleDate';
import formatDate from '../Functions/Traitement/fomatDate';
import { t } from 'i18next';
import DateRangeFilter from './DateRangeFilter';

// Workaround for recharts PolarAngleAxis TS2786 type incompatibility
const PolarAngleAxis = _PolarAngleAxis as any;

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const STATUT_COLORS = ['#f59e0b', '#3b82f6', '#10b981'];

const tooltipStyle = {
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 12,
  backgroundColor: '#fff',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
};

export default function Dashbord() {
  const authHeader = useAuthHeader();
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const [load, setLoad] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const currentYear = new Date().getFullYear();
  const [intervale, setIntervale] = React.useState<IntervaleDate>({
    from: `${currentYear}-01-01`,
    to: user.datjour ? formatDate(user.datjour) : '',
  });

  // KPI states - Financement & Membres
  const [kpiGlobal, setKpiGlobal] = React.useState<any>(null);
  const [kpiParProduit, setKpiParProduit] = React.useState<any[]>([]);
  const [repartitionEtape, setRepartitionEtape] = React.useState<any[]>([]);
  const [tendanceMensuelle, setTendanceMensuelle] = React.useState<any[]>([]);
  const [repartitionAgence, setRepartitionAgence] = React.useState<any[]>([]);
  const [dossiersAttente, setDossiersAttente] = React.useState<any[]>([]);
  const [ouvertureComptes, setOuvertureComptes] = React.useState<any[]>([]);
  const [nbClients, setNbClients] = React.useState<any[]>([]);

  // KPI states - Opérations
  const [kpiOpGlobal, setKpiOpGlobal] = React.useState<any>(null);
  const [kpiParTypeOp, setKpiParTypeOp] = React.useState<any[]>([]);
  const [kpiParCodeOp, setKpiParCodeOp] = React.useState<any[]>([]);
  const [repartitionSensStatut, setRepartitionSensStatut] = React.useState<any[]>([]);
  const [tendanceMensuelleOp, setTendanceMensuelleOp] = React.useState<any[]>([]);
  const [repartitionAgenceOp, setRepartitionAgenceOp] = React.useState<any[]>([]);
  const [operationsAttente, setOperationsAttente] = React.useState<any[]>([]);
  const [operationsLitige, setOperationsLitige] = React.useState<any[]>([]);
  const [top10DernieresOpe, setTop10DernieresOpe] = React.useState<any[]>([]);

  // KPI states - Membership (complément)
  const [cptCliParTypePlanGenre, setCptCliParTypePlanGenre] = React.useState<any[]>([]);
  const [top10MeilleursClients, setTop10MeilleursClients] = React.useState<any[]>([]);

  React.useEffect(() => {
    LoadData(true);
  }, [user.coduti, user.codgroup, user.codage, intervale.from, intervale.to, authHeader]);

  function LoadData(first: boolean = false) {
    console.info('[BI DEBUG] dashboard request start', {
      endpoint: datas.ApiUrl.BI,
      userCode: user.coduti ?? null,
      groupCode: user.codgroup ?? null,
      branchCode: user.codage ?? null,
      date: user.datjour ?? null,
      interval: intervale,
    });
    setLoading(first);
    setLoad(true);
    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: datas.ApiUrl.BI,
      headers: {
        Accept: 'text/plain',
        Authorization: authHeader(),
        CypherUser: Crypter.encrypt(JSON.stringify(user)),
        CypherIntervaleDate: Crypter.encrypt(JSON.stringify(intervale as IntervaleDate)),
      },
    };

    axios
      .request(config)
      .then((response: any) => {
        console.info('[BI DEBUG] dashboard response', {
          httpStatus: response.status,
          responseDataType: typeof response.data,
        });
        setLoading(false);
        setLoad(false);
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        console.info('[BI DEBUG] dashboard business result', {
          status: res?.StatutCode ?? null,
          message: res?.Message ?? null,
          resultIsArray: Array.isArray(res?.Result),
          resultLength: Array.isArray(res?.Result) ? res.Result.length : null,
        });
        if (res.StatutCode == 200) {
          // Result[0] - probe santé du module BI (msg/statut) : non exploité côté dashboard

          // Result[1] - Ouverture comptes
          setOuvertureComptes(
            (res.Result[1] || []).map((e: any) => ({
              Agence: e.CODAGE + ' - ' + e.Libelle,
              'Ouverture de comptes': e.NbCompteOuvert,
            }))
          );
          // Result[2] - Clients
          setNbClients(
            (res.Result[2] || []).map((e: any) => ({
              Agence: e.CODAGE + ' - ' + e.LIBELLE,
              'Nombre de clients': e.NbClient,
            }))
          );
          // Result[3] - KPI Global Financement
          setKpiGlobal(res.Result[3]?.[0] || null);
          // Result[4] - KPI par produit
          setKpiParProduit(res.Result[4] || []);
          // Result[5] - Répartition étape/statut
          setRepartitionEtape(res.Result[5] || []);
          // Result[6] - Tendance mensuelle (financement)
          setTendanceMensuelle(res.Result[6] || []);
          // Result[7] - Répartition par agence (financement)
          setRepartitionAgence(res.Result[7] || []);
          // Result[8] - Dossiers en attente
          setDossiersAttente(res.Result[8] || []);
          // Result[9] - Détail brut (financement) : non exploité côté dashboard (export uniquement)

          // Result[10] - KPI Global Opérations
          setKpiOpGlobal(res.Result[10]?.[0] || null);
          // Result[11] - KPI par type d'opération
          setKpiParTypeOp(res.Result[11] || []);
          // Result[12] - KPI par code d'opération
          setKpiParCodeOp(res.Result[12] || []);
          // Result[13] - Répartition sens & statut
          setRepartitionSensStatut(res.Result[13] || []);
          // Result[14] - Tendance mensuelle (opérations)
          setTendanceMensuelleOp(res.Result[14] || []);
          // Result[15] - Répartition par agence (opérations)
          setRepartitionAgenceOp(res.Result[15] || []);
          // Result[16] - Opérations en attente
          setOperationsAttente(res.Result[16] || []);
          // Result[17] - Opérations en litige
          setOperationsLitige(res.Result[17] || []);
          // Result[18] - Détail brut (opérations) : non exploité côté dashboard (export uniquement)
          // Result[19] - Top 10 dernières opérations
          setTop10DernieresOpe(res.Result[19] || []);
          // Result[20] - Comptes par type/plan/genre
          setCptCliParTypePlanGenre(res.Result[20] || []);
          // Result[21] - Top 10 meilleurs clients
          setTop10MeilleursClients(res.Result[21] || []);
        }
      })
      .catch((error) => {
        console.error('[BI DEBUG] dashboard request failed', {
          httpStatus: error?.response?.status ?? null,
          message: error?.message ?? 'unknown error',
        });
        setLoading(false);
        setLoad(false);
      });
  }

  const fmtMoney = (v: number) =>
    v != null ? Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
  const fmtPct = (v: number) => (v != null ? Number(v).toFixed(1) + '%' : '0%');

  // Un jeu de résultat est considéré disponible s'il contient au moins une ligne.
  // Permet de ne jamais mapper/afficher une box quand le KPI correspondant est vide.
  const hasData = (arr: any[] | null | undefined) => Array.isArray(arr) && arr.length > 0;

  // Tendance mensuelle grouped by product
  const tendanceGrouped = React.useMemo(() => {
    const map: Record<string, any> = {};
    tendanceMensuelle.forEach((e: any) => {
      const mois = e.Mois?.substring(0, 7);
      if (!mois) return;
      if (!map[mois]) map[mois] = { Mois: mois };
      map[mois][e.CodeFinancement] = e.NbDossiersCrees;
    });
    return Object.values(map).sort((a: any, b: any) => a.Mois.localeCompare(b.Mois));
  }, [tendanceMensuelle]);

  const produits = React.useMemo(
    () => [...new Set(tendanceMensuelle.map((e: any) => e.CodeFinancement))].filter(Boolean),
    [tendanceMensuelle]
  );

  // Répartition étape/statut grouped
  const repartitionData = React.useMemo(() => {
    const map: Record<string, any> = {};
    repartitionEtape.forEach((e: any) => {
      const key = e.CodeFinancement + ' - ' + e.EtapeActuelle;
      if (!map[key])
        map[key] = { label: key, 'En instruction': 0, 'Validé non exécuté': 0, Exécuté: 0 };
      if (e.StatutGroupe === 'EN_INSTRUCTION') map[key]['En instruction'] = e.NbDossiers;
      else if (e.StatutGroupe === 'VALIDE_NON_EXECUTE') map[key]['Validé non exécuté'] = e.NbDossiers;
      else if (e.StatutGroupe === 'EXECUTE') map[key].Exécuté = e.NbDossiers;
    });
    return Object.values(map);
  }, [repartitionEtape]);

  // Répartition agence grouped
  const repartitionAgenceData = React.useMemo(() => {
    const map: Record<string, any> = {};
    repartitionAgence.forEach((e: any) => {
      if (!e.CODAGE) return;
      if (!map[e.CODAGE]) map[e.CODAGE] = { Agence: e.CODAGE };
      map[e.CODAGE][e.CodeFinancement] = e.MontantDemandeCumule;
    });
    return Object.values(map);
  }, [repartitionAgence]);

  const produitsAgence = React.useMemo(
    () => [...new Set(repartitionAgence.map((e: any) => e.CodeFinancement))].filter(Boolean),
    [repartitionAgence]
  );

  // Répartition sens & statut grouped for stacked bar chart
  const sensStatutData = React.useMemo(() => {
    const map: Record<string, any> = {};
    repartitionSensStatut.forEach((e: any) => {
      const key = e.SensOperation || e.Sens || 'N/A';
      if (!map[key]) map[key] = { Sens: key };
      const statutKey = e.StatutOperation || e.Statut || 'Inconnu';
      map[key][statutKey] = (map[key][statutKey] || 0) + (e.NbOperations || 0);
    });
    return Object.values(map);
  }, [repartitionSensStatut]);

  const statutsSens = React.useMemo(
    () => [...new Set(repartitionSensStatut.map((e: any) => e.StatutOperation || e.Statut || 'Inconnu'))],
    [repartitionSensStatut]
  );

  // Radar data: normalized multi-criteria comparison by type
  const radarData = React.useMemo(() => {
    if (kpiParTypeOp.length === 0) return [];
    const maxMontant = Math.max(...kpiParTypeOp.map((e: any) => e.MontantTotal || 0), 1);
    const maxVolume = Math.max(...kpiParTypeOp.map((e: any) => e.NbOperationsTotal || 0), 1);
    const maxTicket = Math.max(...kpiParTypeOp.map((e: any) => e.TicketMoyen || 0), 1);
    const maxLitige = Math.max(...kpiParTypeOp.map((e: any) => e.NbEnLitige || 0), 1);
    const criteria = ['Volume', 'Montant', 'Ticket Moyen', 'Historisation', 'Litiges'];
    return criteria.map((c: string) => {
      const row: any = { critere: c };
      kpiParTypeOp.forEach((op: any) => {
        let val = 0;
        if (c === 'Volume') val = ((op.NbOperationsTotal || 0) / maxVolume) * 100;
        else if (c === 'Montant') val = ((op.MontantTotal || 0) / maxMontant) * 100;
        else if (c === 'Ticket Moyen') val = ((op.TicketMoyen || 0) / maxTicket) * 100;
        else if (c === 'Historisation') val = op.TauxHistorisationPct || 0;
        else if (c === 'Litiges') val = ((op.NbEnLitige || 0) / maxLitige) * 100;
        row[op.FamilleOperation] = Math.round(val);
      });
      return row;
    });
  }, [kpiParTypeOp]);

  const typesOp = React.useMemo(
    () => kpiParTypeOp.map((e: any) => e.FamilleOperation).filter(Boolean),
    [kpiParTypeOp]
  );

  // Top codes d'opération par volume (for horizontal bar chart)
  const topCodeOp = React.useMemo(() => {
    return [...kpiParCodeOp]
      .sort((a: any, b: any) => (b.NbOperationsTotal || 0) - (a.NbOperationsTotal || 0))
      .slice(0, 10)
      .map((e: any) => ({
        ...e,
        LibelleCourt: (e.LibelleCodeOperation || e.CodeOperation || '').length > 25
          ? (e.LibelleCodeOperation || '').substring(0, 25) + '…'
          : (e.LibelleCodeOperation || e.CodeOperation || ''),
      }));
  }, [kpiParCodeOp]);

  // Tendance mensuelle grouped by famille d'opération
  const tendanceGroupedOp = React.useMemo(() => {
    const map: Record<string, any> = {};
    tendanceMensuelleOp.forEach((e: any) => {
      const mois = e.Mois?.substring(0, 7);
      if (!mois) return;
      if (!map[mois]) map[mois] = { Mois: mois };
      map[mois][e.FamilleOperation] = e.NbOperationsSaisies;
    });
    return Object.values(map).sort((a: any, b: any) => a.Mois.localeCompare(b.Mois));
  }, [tendanceMensuelleOp]);

  const famillesOpTendance = React.useMemo(
    () => [...new Set(tendanceMensuelleOp.map((e: any) => e.FamilleOperation))].filter(Boolean),
    [tendanceMensuelleOp]
  );

  // Répartition agence opérations grouped
  const repartitionAgenceOpData = React.useMemo(() => {
    const map: Record<string, any> = {};
    repartitionAgenceOp.forEach((e: any) => {
      if (!e.AgenceCode) return;
      if (!map[e.AgenceCode]) map[e.AgenceCode] = { Agence: e.AgenceCode };
      map[e.AgenceCode][e.FamilleOperation] = e.NbOperations;
    });
    return Object.values(map);
  }, [repartitionAgenceOp]);

  const famillesOpAgence = React.useMemo(
    () => [...new Set(repartitionAgenceOp.map((e: any) => e.FamilleOperation))].filter(Boolean),
    [repartitionAgenceOp]
  );

  const statutColors: Record<string, string> = {
    VALIDEE: 'blue',
    EN_ANALYSE: 'orange',
    REMIS: '#116599',
    LIVRE: '#116599',
    REJETEE: 'red',
  };
  const etapeColors: Record<string, string> = {
    DEMANDE: 'blue',
    PROPOSITION: 'cyan',
    VALIDATION: 'orange',
    DECAISSEMENT: 'purple',
    REMISE: '#116599',
    RECEPTION: 'geekblue',
  };

  const columnsAttente = [
    { title: 'N° Demande', dataIndex: 'NumeroDemande', key: 'NumeroDemande', width: 150 },
    { title: 'Produit', dataIndex: 'CodeFinancement', key: 'CodeFinancement', width: 110, render: (v: string) => <Tag color={etapeColors[v] || 'default'}>{v}</Tag> },
    { title: 'Client', dataIndex: 'NOMCLI', key: 'NOMCLI', width: 200 },
    { title: 'Étape', dataIndex: 'EtapeActuelle', key: 'EtapeActuelle', width: 130, render: (v: string) => <Tag color={etapeColors[v] || 'default'}>{v}</Tag> },
    { title: 'Statut', dataIndex: 'StatutDemande', key: 'StatutDemande', width: 120, render: (v: string) => <Tag color={statutColors[v] || 'default'}>{v}</Tag> },
    { title: 'Montant', dataIndex: 'MontantDemande', key: 'MontantDemande', width: 140, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Jours', dataIndex: 'JoursDepuisCreation', key: 'JoursDepuisCreation', width: 80, align: 'center' as const, render: (v: any) => <span style={{ color: v < 0 ? '#999' : v > 5 ? '#f44336' : '#10b981' }}>{v}</span> },
  ];

  // Colonnes pour le tableau des codes d'opérations
  const columnsCodeOp = [
    { title: 'Code', dataIndex: 'CodeOperation', key: 'CodeOperation', width: 80 },
    { title: 'Libellé Opération', dataIndex: 'LibelleCodeOperation', key: 'LibelleCodeOperation', width: 220 },
    { title: 'Type', dataIndex: 'FamilleOperation', key: 'FamilleOperation', width: 90, render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Total Ops', dataIndex: 'NbOperationsTotal', key: 'NbOperationsTotal', width: 100, align: 'right' as const },
    { title: 'Montant Total', dataIndex: 'MontantTotal', key: 'MontantTotal', width: 140, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Ticket Moyen', dataIndex: 'TicketMoyen', key: 'TicketMoyen', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Taux Hist.', dataIndex: 'TauxHistorisationPct', key: 'TauxHistorisationPct', width: 110, align: 'right' as const, render: (v: any) => fmtPct(v) },
  ];

  // Colonnes - Opérations en attente
  const columnsOperationsAttente = [
    { title: 'Agence', dataIndex: 'AgenceCode', key: 'AgenceCode', width: 90 },
    { title: 'Compte', dataIndex: 'NumCompte', key: 'NumCompte', width: 140 },
    { title: 'Type', dataIndex: 'FamilleOperation', key: 'FamilleOperation', width: 90, render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Opération', dataIndex: 'LibelleOperation', key: 'LibelleOperation', width: 200 },
    { title: 'Montant', dataIndex: 'Montant', key: 'Montant', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Sens', dataIndex: 'Sens', key: 'Sens', width: 80, render: (v: string) => <Tag color={v === 'D' || v === 'DEBIT' ? 'volcano' : '#116599'}>{v}</Tag> },
    { title: 'Date saisie', dataIndex: 'DateSaisie', key: 'DateSaisie', width: 110 },
    { title: 'Jours', dataIndex: 'JoursDepuisSaisie', key: 'JoursDepuisSaisie', width: 80, align: 'center' as const, render: (v: any) => <span style={{ color: v > 3 ? '#f44336' : '#10b981' }}>{v}</span> },
    { title: 'Utilisateur', dataIndex: 'UtilisateurSaisie', key: 'UtilisateurSaisie', width: 130 },
  ];

  // Colonnes - Opérations en litige
  const columnsOperationsLitige = [
    { title: 'Agence', dataIndex: 'AgenceCode', key: 'AgenceCode', width: 90 },
    { title: 'Compte', dataIndex: 'NumCompte', key: 'NumCompte', width: 140 },
    { title: 'Type', dataIndex: 'FamilleOperation', key: 'FamilleOperation', width: 90, render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Opération', dataIndex: 'LibelleOperation', key: 'LibelleOperation', width: 180 },
    { title: 'Montant', dataIndex: 'Montant', key: 'Montant', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Motif', dataIndex: 'Motif', key: 'Motif', width: 200 },
    { title: 'Date saisie', dataIndex: 'DateSaisie', key: 'DateSaisie', width: 110 },
    { title: 'Jours', dataIndex: 'JoursDepuisSaisie', key: 'JoursDepuisSaisie', width: 80, align: 'center' as const, render: (v: any) => <span style={{ color: '#f44336' }}>{v}</span> },
    { title: 'Utilisateur', dataIndex: 'UtilisateurSaisie', key: 'UtilisateurSaisie', width: 130 },
  ];

  // Colonnes - Top 10 dernières opérations
  const columnsTop10DernieresOpe = [
    { title: 'Agence', dataIndex: 'AgenceCode', key: 'AgenceCode', width: 90 },
    { title: 'Compte', dataIndex: 'NumCompte', key: 'NumCompte', width: 140 },
    { title: 'Type', dataIndex: 'FamilleOperation', key: 'FamilleOperation', width: 90, render: (v: string) => <Tag color="blue">{v}</Tag> },
    { title: 'Opération', dataIndex: 'LibelleOperation', key: 'LibelleOperation', width: 180 },
    { title: 'Montant', dataIndex: 'Montant', key: 'Montant', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Sens', dataIndex: 'Sens', key: 'Sens', width: 80 },
    { title: 'Date opération', dataIndex: 'DateOperation', key: 'DateOperation', width: 130 },
    { title: 'Statut', dataIndex: 'Statut', key: 'Statut', width: 110, render: (v: string) => <Tag color={statutColors[v] || 'default'}>{v}</Tag> },
  ];

  // Colonnes - Top 10 meilleurs clients
  const columnsTop10Clients = [
    { title: 'N° Client', dataIndex: 'NUMCLI', key: 'NUMCLI', width: 110 },
    { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI', width: 200 },
    { title: 'Épargne', dataIndex: 'TotalEpargne', key: 'TotalEpargne', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Financement', dataIndex: 'TotalFinancement', key: 'TotalFinancement', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Fonds propres', dataIndex: 'TotalFondsPropre', key: 'TotalFondsPropre', width: 130, align: 'right' as const, render: (v: any) => fmtMoney(v) },
    { title: 'Total global', dataIndex: 'TotalGlobal', key: 'TotalGlobal', width: 140, align: 'right' as const, render: (v: any) => <strong>{fmtMoney(v)}</strong> },
  ];

  // Colonnes - Comptes par type/plan/genre
  const columnsCptCli = [
    { title: 'Type de compte', dataIndex: 'TypeCompte', key: 'TypeCompte', width: 140 },
    { title: 'Plan comptable', dataIndex: 'PlanComptable', key: 'PlanComptable', width: 140 },
    { title: 'Genre', dataIndex: 'Genre', key: 'Genre', width: 100 },
    { title: 'Comptes ouverts', dataIndex: 'NbComptesOuverts', key: 'NbComptesOuverts', width: 130, align: 'right' as const },
    { title: 'Clients', dataIndex: 'NbClientsOuverts', key: 'NbClientsOuverts', width: 100, align: 'right' as const },
  ];

  const kpiCards = [
    { icon: <Description />, label: 'Total dossiers', value: kpiGlobal?.NbDossiersTotal ?? 0, sub: `${kpiGlobal?.NbEnInstruction ?? 0} en instruction · ${kpiGlobal?.NbValideesEnAttenteExecution ?? 0} validés`, color: '#3b82f6', bg: 'linear-gradient(135deg, #eff6ff, #dbeafe)' },
    { icon: <MonetizationOn />, label: 'Montant demandé', value: fmtMoney(kpiGlobal?.MontantTotalDemande), sub: `Ticket moyen: ${fmtMoney(kpiGlobal?.TicketMoyenDemande)}`, color: '#10b981', bg: 'linear-gradient(135deg, #ecfdf5, #d1fae5)' },
    { icon: <TrendingUp />, label: "Taux d'approbation", value: fmtPct(kpiGlobal?.TauxApprobationPct), sub: `Taux de rejet: ${fmtPct(kpiGlobal?.TauxRejetPct)}`, color: '#f59e0b', bg: 'linear-gradient(135deg, #fffbeb, #fef3c7)' },
    { icon: <Verified />, label: 'Montant validé', value: fmtMoney(kpiGlobal?.MontantTotalValide), sub: `Pipeline: ${fmtMoney(kpiGlobal?.MontantPipelineEnAttenteExecution)}`, color: '#8b5cf6', bg: 'linear-gradient(135deg, #f5f3ff, #ede9fe)' },
  ];

  const kpiOpCards = [
    { icon: <SwapHoriz />, label: 'Total Opérations', value: kpiOpGlobal?.NbOperationsTotal ?? 0, sub: `Historisées: ${kpiOpGlobal?.NbHistorisees ?? 0} · En attente: ${kpiOpGlobal?.NbEnAttente ?? 0}`, color: '#3b82f6', bg: 'linear-gradient(135deg, #eff6ff, #dbeafe)' },
    { icon: <MonetizationOn />, label: 'Volume Financier Global', value: fmtMoney(kpiOpGlobal?.MontantTotal), sub: `Ticket moyen: ${fmtMoney(kpiOpGlobal?.TicketMoyen)}`, color: '#10b981', bg: 'linear-gradient(135deg, #ecfdf5, #d1fae5)' },
    { icon: <Warning />, label: 'Opérations en Litige', value: kpiOpGlobal?.NbEnLitige ?? 0, sub: `Taux litige: ${fmtPct(kpiOpGlobal?.TauxLitigePct)}`, color: '#ef4444', bg: 'linear-gradient(135deg, #fef2f2, #fee2e2)' },
    { icon: <Verified />, label: "Taux d'historisation", value: fmtPct(kpiOpGlobal?.TauxHistorisationPct), sub: `Montant en attente: ${fmtMoney(kpiOpGlobal?.MontantEnAttente)}`, color: '#8b5cf6', bg: 'linear-gradient(135deg, #f5f3ff, #ede9fe)' },
  ];

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, margin: '0 auto' }}>
      {loading ? (
        <Box>
          <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rounded" height={130} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rounded" height={340} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rounded" height={340} />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box>
          {/* Header + Filter */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {t('texte.bienvenue')} {user.nomuti}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tableau de bord · Indicateurs clés de performance
              </Typography>
            </Box>
            <DateRangeFilter
              value={intervale}
              onChange={(e) => { setIntervale(e); LoadData(false); }}
              onRefresh={() => LoadData(false)}
              loading={load}
              maxDate={user.datjour ? formatDate(user.datjour) : ''}
            />
          </Paper>

          <Tabs
            defaultActiveKey="1"
            size="large"
            type="card"
            items={[
              {
                key: '1',
                label: 'Financement & investissement',
                children: (
                  <Box sx={{ pt: 1 }}>
                    {/* KPI Global Cards */}
                    {kpiGlobal && (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {kpiCards.map((kpi, i) => (
                          <Grid item xs={12} sm={6} md={3} key={i}>
                            <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 2, bgcolor: kpi.bg, color: kpi.color }}>
                                  {kpi.icon}
                                </Box>
                                <Typography variant="body2" color="textSecondary" fontWeight={600}>
                                  {kpi.label}
                                </Typography>
                              </Box>
                              <Typography variant="h4" fontWeight="bold" sx={{ color: kpi.color, lineHeight: 1.2 }}>
                                {kpi.value}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                {kpi.sub}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {/* KPI par produit */}
                    {hasData(kpiParProduit) && (
                      <Box sx={{ mb: 3 }}>
                        <Divider sx={{ mb: 2 }}>
                          <Typography variant="h6" fontWeight="bold">KPI par produit de financement</Typography>
                        </Divider>
                        <Grid container spacing={2}>
                          {kpiParProduit.map((p: any, i: number) => {
                            const tremorColors = ['blue', 'emerald', 'amber'] as any;
                            const hexColors = ['#3b82f6', '#10b981', '#f59e0b'];
                            return (
                              <Grid item xs={12} sm={6} md={4} key={i}>
                                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="h6" fontWeight="bold">{p.CodeFinancement}</Typography>
                                    <Tag color={tremorColors[i % 3]}>{p.NbDossiersTotal} dossiers</Tag>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                    <Box>
                                      <Typography variant="caption" color="textSecondary">Demandé</Typography>
                                      <Typography variant="body2" fontWeight="bold">{fmtMoney(p.MontantTotalDemande)}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="textSecondary">Validé</Typography>
                                      <Typography variant="body2" fontWeight="bold" sx={{ color: hexColors[i % 3] }}>{fmtMoney(p.MontantTotalValide)}</Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="textSecondary">Approbation</Typography>
                                      <Typography variant="body2" fontWeight="bold">{fmtPct(p.TauxApprobationPct)}</Typography>
                                    </Box>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={p.TauxApprobationPct || 0}
                                    sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: hexColors[i % 3] } }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}

                    {/* Charts row 1 */}
                    {hasData(tendanceGrouped) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Tendance mensuelle par produit</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Nombre de dossiers créés par mois</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <ReAreaChart data={tendanceGrouped} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                  {produits.map((p: string, i: number) => (
                                    <linearGradient key={p} id={`grad-${p}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.3} />
                                      <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                                    </linearGradient>
                                  ))}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="Mois" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                                <RTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                {produits.map((p: string, i: number) => (
                                  <Area key={p} type="monotone" dataKey={p} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={`url(#grad-${p})`} strokeWidth={2} />
                                ))}
                              </ReAreaChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                    )}

                    {/* Charts row 2 */}
                    {hasData(repartitionData) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Répartition par étape et statut</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Nombre de dossiers par produit et étape</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <ReBarChart data={repartitionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" interval={0} angle={-15} textAnchor="end" height={70} />
                                <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                                <RTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="En instruction" fill={STATUT_COLORS[0]} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Validé non exécuté" fill={STATUT_COLORS[1]} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="Exécuté" fill={STATUT_COLORS[2]} radius={[4, 4, 0, 0]} />
                              </ReBarChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                    )}

                    {/* Répartition par agence */}
                    {hasData(repartitionAgenceData) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Répartition par agence et produit</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Montant demandé par agence</Typography>
                          <ResponsiveContainer width="100%" height={300}>
                            <ReBarChart data={repartitionAgenceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="Agence" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                              <RTooltip contentStyle={tooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              {produitsAgence.map((p: string, i: number) => (
                                <Bar key={p} dataKey={p} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
                              ))}
                            </ReBarChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Box>
                    )}

                    {/* Dossiers en attente */}
                    {hasData(dossiersAttente) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Dossiers en attente</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{dossiersAttente.length} dossiers nécessitant une action</Typography>
                          <Box sx={{ overflowX: 'auto' }}>
                            <AntTable
                              dataSource={dossiersAttente}
                              columns={columnsAttente}
                              rowKey="NumeroDemande"
                              size="small"
                              pagination={{ pageSize: 8, showSizeChanger: false }}
                              scroll={{ x: 800 }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                ),
              },
              {
                key: '2',
                label: 'Membership',
                children: (
                  <Box sx={{ pt: 1 }}>
                    <Grid container spacing={2}>
                      {hasData(ouvertureComptes) && (
                      <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Ouverture de comptes</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Répartition par agence</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <RePieChart>
                                <Pie
                                  data={ouvertureComptes}
                                  dataKey="Ouverture de comptes"
                                  nameKey="Agence"
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={100}
                                  paddingAngle={2}
                                >
                                  {ouvertureComptes.map((_: any, i: number) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </Pie>
                                <RTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                              </RePieChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      )}
                      {hasData(nbClients) && (
                      <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Nombre de clients</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Répartition par agence</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                              <RePieChart>
                                <Pie
                                  data={nbClients}
                                  dataKey="Nombre de clients"
                                  nameKey="Agence"
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={100}
                                  paddingAngle={2}
                                >
                                  {nbClients.map((_: any, i: number) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </Pie>
                                <RTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                              </RePieChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      )}
                      {hasData(top10MeilleursClients) && (
                      <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Top 10 meilleurs clients</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Répartition par épargne, financement et fonds propres</Typography>
                          <ResponsiveContainer width="100%" height={400}>
                            <ReBarChart
                              layout="vertical"
                              data={top10MeilleursClients.map((e: any) => ({
                                ...e,
                                Label: `${e.NOMCLI || e.NUMCLI}`,
                                TotalEpargne: Number(e.TotalEpargne) || 0,
                                TotalFinancement: Number(e.TotalFinancement) || 0,
                                TotalFondsPropre: Number(e.TotalFondsPropre) || 0,
                                TotalGlobal: Number(e.TotalGlobal) || 0,
                              })).sort((a: any, b: any) => b.TotalGlobal - a.TotalGlobal)}
                              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v: any) => fmtMoney(v)} />
                              <YAxis type="category" dataKey="Label" tick={{ fontSize: 11 }} stroke="#94a3b8" width={180} tickFormatter={(v: string) => v && v.length > 22 ? v.substring(0, 22) + '…' : v} />
                              <RTooltip
                                contentStyle={tooltipStyle}
                                formatter={(value: any, name: any, props: any) => {
                                  const p = props.payload;
                                  if (name === 'TotalEpargne' || name === 'TotalFinancement' || name === 'TotalFondsPropre') {
                                    return [fmtMoney(value), name === 'TotalEpargne' ? 'Épargne' : name === 'TotalFinancement' ? 'Financement' : 'Fonds propres'];
                                  }
                                  return [fmtMoney(value), name];
                                }}
                                labelFormatter={(_label: any, payload: any) => {
                                  if (payload && payload[0]) {
                                    const p = payload[0].payload;
                                    return `${p.NOMCLI || ''} (N° ${p.NUMCLI}) — Total: ${fmtMoney(p.TotalGlobal)}`;
                                  }
                                  return '';
                                }}
                              />
                              <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value: any) => value === 'TotalEpargne' ? 'Épargne' : value === 'TotalFinancement' ? 'Financement' : value === 'TotalFondsPropre' ? 'Fonds propres' : value} />
                              <Bar dataKey="TotalEpargne" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} barSize={28} />
                              <Bar dataKey="TotalFinancement" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} barSize={28} />
                              <Bar dataKey="TotalFondsPropre" stackId="a" fill="#f59e0b" radius={[0, 6, 6, 0]} barSize={28} />
                            </ReBarChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      )}
                      {hasData(cptCliParTypePlanGenre) && (
                      <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Comptes par type, plan et genre</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Répartition détaillée des comptes ouverts</Typography>
                          <Box sx={{ overflowX: 'auto' }}>
                            <AntTable
                              dataSource={cptCliParTypePlanGenre}
                              columns={columnsCptCli}
                              rowKey={(record: any, idx?: number) => `${record.TypeCompte}-${record.PlanComptable}-${record.Genre}-${idx}`}
                              size="small"
                              pagination={{ pageSize: 8, showSizeChanger: false }}
                              scroll={{ x: 600 }}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                      )}
                    </Grid>
                  </Box>
                ),
              },
              {
                key: '3',
                label: 'Opérations',
                children: (
                  <Box sx={{ pt: 1 }}>
                    {/* KPI Global Opérations */}
                    {kpiOpGlobal && (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {kpiOpCards.map((kpi, i) => (
                          <Grid item xs={12} sm={6} md={3} key={i}>
                            <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 2, bgcolor: kpi.bg, color: kpi.color }}>
                                  {kpi.icon}
                                </Box>
                                <Typography variant="body2" color="textSecondary" fontWeight={600}>
                                  {kpi.label}
                                </Typography>
                              </Box>
                              <Typography variant="h4" fontWeight="bold" sx={{ color: kpi.color, lineHeight: 1.2 }}>
                                {kpi.value}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                {kpi.sub}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {/* KPI par type d'opération */}
                    {hasData(kpiParTypeOp) && (
                      <Box sx={{ mb: 3 }}>
                        <Divider sx={{ mb: 2 }}>
                          <Typography variant="h6" fontWeight="bold">Volume et montants par Type d'Opération</Typography>
                        </Divider>
                        <Grid container spacing={2}>
                          {kpiParTypeOp.map((op: any, i: number) => {
                            const hexColors = ['#3b82f6', '#10b981', '#f59e0b'];
                            return (
                              <Grid item xs={12} sm={4} key={i}>
                                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="h6" fontWeight="bold">{op.FamilleOperation}</Typography>
                                    <Tag color="cyan">{op.NbOperationsTotal} ops</Tag>
                                  </Box>
                                  <Typography variant="caption" color="textSecondary">{op.FamilleOperation}</Typography>
                                  <Box sx={{ mt: 1, mb: 1 }}>
                                    <Typography variant="caption" color="textSecondary">Montant Total</Typography>
                                    <Typography variant="body1" fontWeight="bold" sx={{ color: hexColors[i % hexColors.length] }}>{fmtMoney(op.MontantTotal)}</Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="caption" color="textSecondary">Ticket Moyen: {fmtMoney(op.TicketMoyen)}</Typography>
                                    <Typography variant="caption" color="textSecondary">Litiges: {op.NbEnLitige}</Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={op.TauxHistorisationPct || 0}
                                    sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: hexColors[i % hexColors.length] } }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}

                    {/* Row 1: Pie (volume par type) + Bar (nombre d'ops par type) */}
                    {hasData(kpiParTypeOp) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={5}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Répartition du volume par nature</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Part des opérations par type (en nombre)</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <RePieChart>
                                <Pie
                                  data={kpiParTypeOp}
                                  dataKey="NbOperationsTotal"
                                  nameKey="FamilleOperation"
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={55}
                                  outerRadius={100}
                                  paddingAngle={3}
                                  label={(entry: any) => `${entry.FamilleOperation}: ${entry.NbOperationsTotal}`}
                                >
                                  {kpiParTypeOp.map((_: any, i: number) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </Pie>
                                <RTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                              </RePieChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Volume et Montant par type d'opération</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Comparatif nombre d'opérations vs montant total</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <ReComposedChart data={kpiParTypeOp} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="FamilleOperation" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                                <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} tickFormatter={(v: any) => fmtMoney(v)} />
                                <RTooltip contentStyle={tooltipStyle} formatter={((value: any, name: string) => name === 'Montant Total' ? fmtMoney(value) : value) as any} />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Bar yAxisId="left" dataKey="NbOperationsTotal" name="Nombre d'opérations" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40} />
                                <Line yAxisId="right" type="monotone" dataKey="MontantTotal" name="Montant Total" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                              </ReComposedChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                    )}

                    {/* Row 2: Sens & Statut stacked bar */}
                    {hasData(sensStatutData) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Répartition par sens et statut d'opération</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Nombre d'opérations en cour de comptabilisation par sens (Débit/Crédit) et statut</Typography>
                          <ResponsiveContainer width="100%" height={300}>
                            <ReBarChart data={sensStatutData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="Sens" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                              <RTooltip contentStyle={tooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              {statutsSens.map((s: string, i: number) => (
                                <Bar key={s} dataKey={s} stackId="a" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === statutsSens.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                              ))}
                            </ReBarChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Box>
                    )}

                    {/* Row 3: Radar (multi-criteria) + Bar (ticket moyen) */}
                    {hasData(kpiParTypeOp) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={7}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Analyse multi-critères par type</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Comparaison normalisée (0-100%) sur 5 dimensions</Typography>
                            <ResponsiveContainer width="100%" height={320}>
                              <ReRadarChart data={radarData}>
                                <PolarGrid stroke="#e2e8f0" />
                                <PolarAngleAxis dataKey="critere" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                                {typesOp.map((t: string, i: number) => (
                                  <Radar key={t} name={t} dataKey={t} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.15} strokeWidth={2} />
                                ))}
                                <RTooltip contentStyle={tooltipStyle} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                              </ReRadarChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Ticket moyen par nature</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Montant moyen par opération</Typography>
                            <ResponsiveContainer width="100%" height={320}>
                              <ReBarChart data={kpiParTypeOp} layout="vertical" margin={{ top: 10, right: 20, left: 80, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v: any) => fmtMoney(v)} />
                                <YAxis type="category" dataKey="FamilleOperation" tick={{ fontSize: 12 }} stroke="#94a3b8" width={70} />
                                <RTooltip contentStyle={tooltipStyle} formatter={(v: any) => fmtMoney(v)} />
                                <Bar dataKey="TicketMoyen" name="Ticket Moyen" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={30} />
                              </ReBarChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                    </Grid>
                    )}

                    {/* Row 4: Historisation radial + Top codes horizontal bar */}
                    {(hasData(kpiParTypeOp) || hasData(topCodeOp)) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {hasData(kpiParTypeOp) && (
                      <Grid item xs={12} md={5}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Taux d'historisation par type</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Pourcentage des opérations historisées</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <ReRadialBarChart data={kpiParTypeOp} innerRadius="20%" outerRadius="90%" startAngle={90} endAngle={-270}>
                                <PolarGrid stroke="#e2e8f0" />
                                <PolarAngleAxis dataKey="FamilleOperation" tick={{ fontSize: 11, fill: '#64748b' }} />
                                <RTooltip contentStyle={tooltipStyle} formatter={(v: any) => fmtPct(v)} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <RadialBar dataKey="TauxHistorisationPct" name="Taux Hist." cornerRadius={6} background>
                                  {kpiParTypeOp.map((_: any, i: number) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                  ))}
                                </RadialBar>
                              </ReRadialBarChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      )}
                      {hasData(topCodeOp) && (
                      <Grid item xs={12} md={7}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                          <Typography variant="h6" fontWeight="bold">Top 10 codes d'opérations par volume</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Codes les plus actifs en nombre d'opérations</Typography>
                            <ResponsiveContainer width="100%" height={280}>
                              <ReBarChart data={topCodeOp} layout="vertical" margin={{ top: 10, right: 20, left: 120, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                                <YAxis type="category" dataKey="LibelleCourt" tick={{ fontSize: 10 }} stroke="#94a3b8" width={120} />
                                <RTooltip contentStyle={tooltipStyle} />
                                <Bar dataKey="NbOperationsTotal" name="Nb Opérations" fill="#06b6d4" radius={[0, 4, 4, 0]} barSize={18} />
                              </ReBarChart>
                            </ResponsiveContainer>
                        </Paper>
                      </Grid>
                      )}
                    </Grid>
                    )}

                    {/* Row 5: Litiges par type bar chart */}
                    {hasData(kpiParTypeOp) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Analyse des litiges par nature d'opération</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Nombre de litiges et taux de litige par type</Typography>
                          <ResponsiveContainer width="100%" height={260}>
                            <ReComposedChart data={kpiParTypeOp} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="FamilleOperation" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#94a3b8" width={60} tickFormatter={(v: any) => `${v}%`} />
                              <RTooltip contentStyle={tooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              <Bar yAxisId="left" dataKey="NbEnLitige" name="Nb Litiges" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={40} />
                              <Line yAxisId="right" type="monotone" dataKey="TauxLitigePct" name="Taux Litige (%)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                            </ReComposedChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Box>
                    )}

                    {/* Tendance mensuelle opérations */}
                    {hasData(tendanceGroupedOp) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Tendance mensuelle des opérations</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Nombre d'opérations saisies par mois et par nature</Typography>
                          <ResponsiveContainer width="100%" height={300}>
                            <ReAreaChart data={tendanceGroupedOp} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <defs>
                                {famillesOpTendance.map((f: string, i: number) => (
                                  <linearGradient key={f} id={`grad-op-${f}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                                  </linearGradient>
                                ))}
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="Mois" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                              <RTooltip contentStyle={tooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              {famillesOpTendance.map((f: string, i: number) => (
                                <Area key={f} type="monotone" dataKey={f} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={`url(#grad-op-${f})`} strokeWidth={2} />
                              ))}
                            </ReAreaChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Box>
                    )}

                    {/* Répartition par agence - opérations */}
                    {hasData(repartitionAgenceOpData) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Répartition des opérations par agence</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Nombre d'opérations par agence et par nature</Typography>
                          <ResponsiveContainer width="100%" height={300}>
                            <ReBarChart data={repartitionAgenceOpData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="Agence" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" width={50} />
                              <RTooltip contentStyle={tooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              {famillesOpAgence.map((f: string, i: number) => (
                                <Bar key={f} dataKey={f} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
                              ))}
                            </ReBarChart>
                          </ResponsiveContainer>
                        </Paper>
                      </Box>
                    )}

                    {/* Opérations en attente */}
                    {hasData(operationsAttente) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Assignment fontSize="small" color="action" />
                            <Typography variant="h6" fontWeight="bold">Opérations en attente</Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{operationsAttente.length} opérations nécessitant un traitement</Typography>
                          <Box sx={{ overflowX: 'auto' }}>
                            <AntTable
                              dataSource={operationsAttente}
                              columns={columnsOperationsAttente}
                              rowKey={(record: any, idx?: number) => `${record.NumCompte}-${record.CodeOperation}-${idx}`}
                              size="small"
                              pagination={{ pageSize: 8, showSizeChanger: false }}
                              scroll={{ x: 900 }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Opérations en litige */}
                    {hasData(operationsLitige) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Warning fontSize="small" color="error" />
                            <Typography variant="h6" fontWeight="bold">Opérations en litige</Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{operationsLitige.length} opérations à régulariser</Typography>
                          <Box sx={{ overflowX: 'auto' }}>
                            <AntTable
                              dataSource={operationsLitige}
                              columns={columnsOperationsLitige}
                              rowKey={(record: any, idx?: number) => `${record.NumCompte}-${record.CodeOperation}-${idx}`}
                              size="small"
                              pagination={{ pageSize: 8, showSizeChanger: false }}
                              scroll={{ x: 900 }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    )}

                    {/* Top 10 dernières opérations - Débit & Crédit séparés */}
                    {hasData(top10DernieresOpe) && (() => {
                      const opsDebit = top10DernieresOpe
                        .filter((e: any) => e.Sens === 'D' || e.Sens === 'DEBIT')
                        .map((e: any) => ({
                          ...e,
                          Label: `${e.LibelleOperation || e.FamilleOperation || 'Op'} · ${e.NumCompte || ''}`,
                          Montant: Number(e.Montant) || 0,
                        }))
                        .sort((a: any, b: any) => b.Montant - a.Montant);
                      const opsCredit = top10DernieresOpe
                        .filter((e: any) => e.Sens === 'C' || e.Sens === 'CREDIT')
                        .map((e: any) => ({
                          ...e,
                          Label: `${e.LibelleOperation || e.FamilleOperation || 'Op'} · ${e.NumCompte || ''}`,
                          Montant: Number(e.Montant) || 0,
                        }))
                        .sort((a: any, b: any) => b.Montant - a.Montant);
                      const tooltipFormatter = (value: any, _name: any, props: any) => {
                        const p = props.payload;
                        return [
                          <div style={{ lineHeight: 1.6 }}>
                            <div><strong>{p.LibelleOperation || p.FamilleOperation}</strong></div>
                            <div>Montant: <strong>{fmtMoney(p.Montant)}</strong></div>
                            <div>Agence: {p.AgenceCode}</div>
                            <div>Compte: {p.NumCompte}</div>
                            <div>Type: {p.FamilleOperation}</div>
                            <div>Date: {p.DateOperation || p.DateSaisie || ''}</div>
                            <div>Statut: {p.Statut || ''}</div>
                          </div>,
                        ];
                      };
                      return (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Top 10 dernières opérations</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Opérations les plus récentes séparées par sens</Typography>
                          <Grid container spacing={2}>
                            {/* Débit */}
                            <Grid item xs={12} md={6}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: '#ef4444' }} />
                                <Typography variant="subtitle2" fontWeight="bold" color="#ef4444">Débit ({opsDebit.length})</Typography>
                              </Box>
                              {opsDebit.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                  <ReBarChart layout="vertical" data={opsDebit} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v: any) => fmtMoney(v)} />
                                    <YAxis type="category" dataKey="Label" tick={{ fontSize: 10 }} stroke="#94a3b8" width={160} tickFormatter={(v: string) => v && v.length > 20 ? v.substring(0, 20) + '…' : v} />
                                    <RTooltip contentStyle={tooltipStyle} formatter={tooltipFormatter} />
                                    <Bar dataKey="Montant" fill="#ef4444" radius={[0, 6, 6, 0]} barSize={24} />
                                  </ReBarChart>
                                </ResponsiveContainer>
                              ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                                  <Typography color="textSecondary">Aucune opération de débit</Typography>
                                </Box>
                              )}
                            </Grid>
                            {/* Crédit */}
                            <Grid item xs={12} md={6}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: 2, bgcolor: '#10b981' }} />
                                <Typography variant="subtitle2" fontWeight="bold" color="#10b981">Crédit ({opsCredit.length})</Typography>
                              </Box>
                              {opsCredit.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                  <ReBarChart layout="vertical" data={opsCredit} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v: any) => fmtMoney(v)} />
                                    <YAxis type="category" dataKey="Label" tick={{ fontSize: 10 }} stroke="#94a3b8" width={160} tickFormatter={(v: string) => v && v.length > 20 ? v.substring(0, 20) + '…' : v} />
                                    <RTooltip contentStyle={tooltipStyle} formatter={tooltipFormatter} />
                                    <Bar dataKey="Montant" fill="#10b981" radius={[0, 6, 6, 0]} barSize={24} />
                                  </ReBarChart>
                                </ResponsiveContainer>
                              ) : (
                                <Box display="flex" justifyContent="center" alignItems="center" height={350}>
                                  <Typography color="textSecondary">Aucune opération de crédit</Typography>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>
                      );
                    })()}

                    {/* Tableau détaillé des codes d'opérations */}
                    {hasData(kpiParCodeOp) && (
                      <Box sx={{ mb: 3 }}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" fontWeight="bold">Détail par Code d'Opération</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Performances et volumes ventilés par code d'opération spécifique</Typography>
                          <Box sx={{ overflowX: 'auto' }}>
                            <AntTable
                              dataSource={kpiParCodeOp}
                              columns={columnsCodeOp}
                              rowKey={(record) => record.CodeOperation + record.FamilleOperation}
                              size="small"
                              pagination={{ pageSize: 6, showSizeChanger: false }}
                              scroll={{ x: 800 }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                ),
              },
            ]}
          />
        </Box>
      )}
    </Box>
  );
}